import { yupResolver } from '@hookform/resolvers/yup';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import Close from '@mui/icons-material/Close';
import {
	useGetClockListQuery,
	useSetDefaultClockMutation,
} from '@/api/app-setting/clockApiSlice';
import CloseIcon from '@mui/icons-material/Close';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { ClockRequest } from '@/api/DTO/app-setting/clock.interface';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { Form } from 'react-router-dom';

interface Props {
	reSetOpen: boolean;
	setReSetOpen: any;
	type: FormType;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function ReSetDefaultClockDialog(props: Props) {
	const { reSetOpen, setReSetOpen, type, setType } = props;
	const { t } = useTranslation();
	const [setDefaultClock] = useSetDefaultClockMutation();
	const defaultValues: DefaultValues<ClockRequest> = {};

	const { data: clockList } = useGetClockListQuery(
		{ pagination: { page: 1, size: 100000 }, sortings: [] },
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const formSchema = Yup.object().shape({
		id: Yup.string().required(t('errorMessage.pleaseSelect')),
	});

	const methods = useForm<{ id: string }>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		const obj = clockList?.data.items.find((item) => item.isDefault == 1);
		methods.setValue('id', obj?.id || '');
	}, [clockList]);

	const handleClose = () => {
		methods.reset();
		setReSetOpen(false);
		setType(FormType.edit);
	};

	const onSubmit: SubmitHandler<{ id: string }> = async (data) => {
		const response = await setDefaultClock(data.id);
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.setClockSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	return (
		<>
			<Dialog
				open={reSetOpen}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xs'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{type == FormType.create
						? t('appSetting.setClock')
						: t('appSetting.reSetClock')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={2} marginTop={0}>
								<Grid item xs={12}>
									{type !== FormType.create && (
										<Typography variant='body1'>
											{t('appSetting.pleaseReSetClock')}
										</Typography>
									)}

									<AppDropDownMenu
										control={methods.control}
										name='id'
										label={t('appSetting.selectClock')}
										options={(clockList?.data.items || []).map((item) => {
											return {
												id: item.id,
												name: item.fileName,
											};
										})}
										required
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.isConfirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
